import type {
  CompetitorService,
  ExternalSolutionKind,
} from "@shared/types/competitor-service";

/// 外部ソリューションマスター (物理名 competitor_services) の一覧フィルタ・表示・
/// 名前バリデーション・保存反映を扱う純粋ロジック。一覧 / 詳細 / 追加編集モーダルで
/// 同じ判定を共有し、UI コンポーネントから副作用を切り離して単体テストできるようにする。

export type ExternalSolutionKindFilter =
  | "all"
  | "competitor"
  | "referral"
  | "other";

export const EXTERNAL_SOLUTION_KIND_OPTIONS: Array<{
  value: ExternalSolutionKind;
  label: string;
}> = [
  { value: "competitor", label: "競合" },
  { value: "referral", label: "紹介先" },
  { value: "other", label: "その他" },
];

/** 一覧フィルタが参照する最小フィールド。 `CompetitorService` も構造的に満たす。 */
export interface ThirdPartySolutionFilterInput {
  name: string;
  /** 外部ソリューション本体に保存された排他的な区分。未指定は `other` として扱う。 */
  kind?: ExternalSolutionKind | null;
  /** 区分と独立した詳細設定。競合区分の時だけ通常表示に使う。 */
  competitor_product_names?: readonly string[] | null;
}

/**
 * PATCH `/api/competitor-services/{id}` の応答に含まれるマスター項目。
 *
 * 応答 DTO は `competitor_product_names` / `usage_target_count` (派生サマリ) を
 * 返さないため、一覧 / 詳細へ反映する際はマスター項目のみ上書きし、派生フィールドは
 * 既存値を温存する ([[applyCompetitorServiceMasterFields]] 参照)。
 */
export interface CompetitorServiceMasterFields {
  id: string;
  name: string;
  memo: string | null;
  kind: ExternalSolutionKind;
  provider_company_name: string | null;
  summary: string | null;
  service_site_url: string | null;
  price_detail: string | null;
  is_curated: boolean;
  updated_at: string;
}

/** 自社ソリューション名と衝突した外部ソリューション名を弾くときの既定メッセージ。 */
export const SAME_AS_PRODUCT_NAME_MESSAGE =
  "自社ソリューション名と同名の外部ソリューションは登録できません";
export const THIRD_PARTY_SOLUTION_NAME_MAX_LENGTH = 120;

const LEGACY_THIRD_PARTY_SOLUTION_LABEL = "他社ソリューション";
const EXTERNAL_SOLUTION_LABEL = "外部ソリューション";

export function normalizeThirdPartySolutionErrorMessage(
  message: string,
): string {
  return message.replaceAll(
    LEGACY_THIRD_PARTY_SOLUTION_LABEL,
    EXTERNAL_SOLUTION_LABEL,
  );
}

/**
 * 外部ソリューション区分。フィルタ入力は構造的な部分型
 * (`ThirdPartySolutionFilterInput`) を受けるため、kind 未指定は `その他` に倒す。
 */
export function externalSolutionKind(
  item: ThirdPartySolutionFilterInput,
): ExternalSolutionKind {
  return item.kind ?? "other";
}

export function externalSolutionKindLabel(kind: ExternalSolutionKind): string {
  switch (kind) {
    case "competitor":
      return "競合";
    case "referral":
      return "紹介先";
    case "other":
      return "その他";
  }
}

/**
 * 一覧の検索文字列 + 外部ソリューション区分フィルタ (純粋関数)。
 *
 * - 検索は外部ソリューション名の大文字小文字を無視した部分一致。空文字は全件。
 * - 区分は外部ソリューション本体の保存値 (`kind`) を正とする。
 */
export function filterThirdPartySolutions<
  T extends ThirdPartySolutionFilterInput,
>(
  items: readonly T[],
  query: string,
  kindFilter: ExternalSolutionKindFilter,
): T[] {
  const needle = query.trim().toLowerCase();
  return items.filter((item) => {
    const kind = externalSolutionKind(item);
    if (kindFilter !== "all" && kind !== kindFilter) return false;
    if (needle.length === 0) return true;
    return item.name.toLowerCase().includes(needle);
  });
}

export function isCompetitorKind(item: ThirdPartySolutionFilterInput): boolean {
  return externalSolutionKind(item) === "competitor";
}

export function isReferralKind(item: ThirdPartySolutionFilterInput): boolean {
  return externalSolutionKind(item) === "referral";
}

/** 競合ソリューション列の要約。先頭名 + 残り件数 (例: `契約管理支援 +2`)。 */
export function thirdPartySettingLabel(
  competitorProductNames?: readonly string[] | null,
): string {
  const names = competitorProductNames ?? [];
  if (names.length === 0) return "なし";
  const first = names[0] ?? "なし";
  const rest = names.length - 1;
  return rest > 0 ? `${first} +${rest}` : first;
}

export function activeCompetitorSettingLabel(
  item: ThirdPartySolutionFilterInput,
): string {
  if (!isCompetitorKind(item)) return "なし";
  return thirdPartySettingLabel(item.competitor_product_names);
}

function normalizeName(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * 外部ソリューション名の保存前バリデーション。作成 / 編集モーダル・一覧インライン編集・
 * 詳細インライン編集で同じ意味を共有する単一ロジック。問題があればエラーメッセージ、
 * 問題なければ `null` を返す。
 *
 * - 空文字 (空白のみ含む) は不可。
 * - 前後空白を除いた名称は 120 文字以内。
 * - `blockedNames` (自社ソリューション名) と大文字小文字・前後空白を無視して一致したら不可。
 */
export function validateThirdPartySolutionName(
  name: string,
  blockedNames: readonly string[] = [],
  blockedNameMessage: string = SAME_AS_PRODUCT_NAME_MESSAGE,
): string | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return "外部ソリューション名を入力してください";
  }
  if (trimmed.length > THIRD_PARTY_SOLUTION_NAME_MAX_LENGTH) {
    return `外部ソリューション名は${THIRD_PARTY_SOLUTION_NAME_MAX_LENGTH}文字以内で入力してください`;
  }
  const normalized = normalizeName(trimmed);
  if (blockedNames.some((blocked) => normalizeName(blocked) === normalized)) {
    return blockedNameMessage;
  }
  return null;
}

/**
 * PATCH 応答 (派生サマリ無し) を 1 行へ反映する。マスター項目のみ上書きし、
 * `competitor_product_names` / `usage_target_count` などの派生フィールドは温存する。
 */
export function applyCompetitorServiceMasterFields(
  target: CompetitorService,
  saved: CompetitorServiceMasterFields,
): CompetitorService {
  return {
    ...target,
    name: saved.name,
    memo: saved.memo,
    kind: saved.kind,
    provider_company_name: saved.provider_company_name,
    summary: saved.summary,
    service_site_url: saved.service_site_url,
    price_detail: saved.price_detail,
    is_curated: saved.is_curated,
    updated_at: saved.updated_at,
  };
}

/**
 * 一覧行配列へ PATCH 応答を反映する。id 一致行だけマスター項目を上書きし、派生フィールドは
 * 温存する。保存後に一覧と詳細の表示を矛盾させないための単一導線。
 */
export function mergeThirdPartySolutionSummary(
  rows: readonly CompetitorService[],
  saved: CompetitorServiceMasterFields,
): CompetitorService[] {
  return rows.map((row) =>
    row.id === saved.id ? applyCompetitorServiceMasterFields(row, saved) : row,
  );
}
